import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { volunteerHouseLabel } from "@shared/helpers/summer-campaign-lead-admin";
import { type FC, type ReactNode } from "react";
import { LeadHandlingControls } from "../../../components/lead-handling/LeadHandlingControls";
import PhoneNumberCell from "../PhoneNumberCell";

export type LeadContextData = {
  id: string;
  phoneNumber: string;
  regionLabel: string;
  attendanceLabel: string;
  teamNames: string[];
  categoryNames: string[];
  availabilityStart?: string | Date | null;
  availabilityEnd?: string | Date | null;
  volunteerHouse?: boolean;
  contactConsent: boolean;
  handlingManagerId?: string | null;
  handlingManagerName?: string | null;
  handlingSince?: string | Date | null;
};

function formatDate(value: string | Date | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("he-IL");
}

type Props = {
  lead: LeadContextData;
  compact?: boolean;
  footer?: ReactNode;
  onHandlingChanged?: () => void;
};

export const LeadContextPanel: FC<Props> = ({
  lead,
  compact = false,
  footer,
  onHandlingChanged,
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 1.5 : 2,
        borderRadius: "1rem",
        bgcolor: "grey.50",
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
        minWidth: compact ? 220 : 260,
        maxWidth: compact ? 280 : 300,
      }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        fontWeight={600}
        sx={{ letterSpacing: 0.5 }}
      >
        העדפות הליד
      </Typography>

      <Box mt={1}>
        <PhoneNumberCell phoneNumber={lead.phoneNumber} />
      </Box>

      <Stack direction="row" gap={0.5} flexWrap="wrap" mt={1.5}>
        <Chip size="small" label={lead.regionLabel} />
        <Chip size="small" label={lead.attendanceLabel} variant="outlined" />
        <Chip
          size="small"
          label={lead.contactConsent ? "הסכמה ליצירת קשר" : "אין הסכמה"}
          color={lead.contactConsent ? "success" : "warning"}
          variant="outlined"
        />
      </Stack>

      {lead.teamNames.length > 0 && (
        <Stack mt={1.5} gap={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            צוותים
          </Typography>
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {lead.teamNames.map((name) => (
              <Chip key={name} size="small" label={name} />
            ))}
          </Stack>
        </Stack>
      )}

      {lead.categoryNames.length > 0 && (
        <Stack mt={1.5} gap={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            קטגוריות
          </Typography>
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {lead.categoryNames.map((name) => (
              <Chip key={name} size="small" label={name} color="secondary" variant="outlined" />
            ))}
          </Stack>
        </Stack>
      )}

      <Stack direction="row" gap={0.75} alignItems="center" mt={1.5}>
        <CalendarMonthIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
        <Typography variant="body2" color="text.secondary">
          {formatDate(lead.availabilityStart)} - {formatDate(lead.availabilityEnd)}
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mt={1}>
        בית מתנדבים: {volunteerHouseLabel(lead.volunteerHouse ?? false)}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <LeadHandlingControls
        leadId={lead.id}
        handlingManagerId={lead.handlingManagerId}
        handlingManagerName={lead.handlingManagerName}
        handlingSince={lead.handlingSince}
        variant="compact"
        onChanged={onHandlingChanged}
      />

      {footer}
    </Paper>
  );
};
